import { useMutation, useQuery } from "@apollo/client/react"
import { useState } from "react"
import { gql } from "~/__generated__"
import { ContactMessageStatus, InternalAdminContactMessagesQuery } from "~/__generated__/graphql"
import { cn } from "~/common/cn"
import { useDocumentTitle } from "~/common/use-document-title"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Badge } from "~/ui/badge"
import { Button } from "~/ui/button"
import { Card, CardContent } from "~/ui/card"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { Tabs, TabsList, TabsTrigger } from "~/ui/tabs"
import { Textarea } from "~/ui/textarea"

const contactMessagesQuery = gql(/* GraphQL */ `
  query InternalAdminContactMessages($status: ContactMessageStatus) {
    internalAdminContactMessages(first: 50, status: $status) {
      nodes {
        id
        name
        email
        subject
        message
        status
        createdAt
        puzzleWpId
        submitterEmail
        replies {
          id
          body
          direction
          createdAt
          fromName
          fromEmail
          senderEmail
          senderName
        }
      }
    }
  }
`)

const updateStatusMutation = gql(/* GraphQL */ `
  mutation InternalAdminContactMessageUpdateStatus(
    $input: InternalAdminContactMessageUpdateStatusInput!
  ) {
    internalAdminContactMessageUpdateStatus(input: $input) {
      contactMessage {
        id
        status
      }
    }
  }
`)

const replyCreateMutation = gql(/* GraphQL */ `
  mutation InternalAdminContactMessageReplyCreate(
    $input: InternalAdminContactMessageReplyCreateInput!
  ) {
    internalAdminContactMessageReplyCreate(input: $input) {
      contactMessage {
        id
        status
        replies {
          id
          body
          direction
          createdAt
          fromName
          fromEmail
          senderEmail
          senderName
        }
      }
    }
  }
`)

type Message = InternalAdminContactMessagesQuery["internalAdminContactMessages"]["nodes"][number]
type Reply = Message["replies"][number]

type FilterValue = "ALL" | ContactMessageStatus

const filters: Array<{ value: FilterValue; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "NEW", label: "New" },
  { value: "REPLIED", label: "Replied" },
  { value: "CLOSED", label: "Closed" },
]

const statusBadgeVariant = (status: ContactMessageStatus) => {
  switch (status) {
    case "NEW":
      return "default"
    case "REPLIED":
      return "secondary"
    case "CLOSED":
      return "outline"
    default:
      throw status satisfies never
  }
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

const senderLabel = (reply: Reply) => {
  if (reply.direction === "OUTBOUND") {
    return reply.senderName || reply.senderEmail || "Wandering Puzzles"
  }
  return reply.fromName || reply.fromEmail || "Sender"
}

export const ContactMessagesScreen = () => {
  useDocumentTitle("Contact messages")
  const [filter, setFilter] = useState<FilterValue>("NEW")
  const { data, loading, error } = useQuery(contactMessagesQuery, {
    variables: { status: filter === "ALL" ? null : filter },
  })
  const [updateStatus, { loading: updating }] = useMutation(updateStatusMutation, {
    onError: () => null,
  })

  const messages = data?.internalAdminContactMessages.nodes ?? []

  return (
    <TablePageLayout>
      <h1 className="text-2xl">Contact messages</h1>
      <hr className="my-4 border-0 border-b" />

      <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterValue)}>
        <TabsList>
          {filters.map((f) => (
            <TabsTrigger key={f.value} value={f.value}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-6">
        {!data && loading ? (
          <ScreenLoading />
        ) : error ? (
          <GraphqlError error={error} />
        ) : messages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-gray-600">
              No messages.
            </CardContent>
          </Card>
        ) : (
          <ul className="grid gap-3">
            {messages.map((m) => (
              <li key={m.id}>
                <Card>
                  <CardContent className="space-y-3 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{m.name}</span>
                          <Badge variant={statusBadgeVariant(m.status)}>{m.status}</Badge>
                        </div>
                        <div className="text-sm text-gray-600">{m.email}</div>
                        {m.submitterEmail && m.submitterEmail !== m.email ? (
                          <div className="text-xs text-gray-500">
                            signed in as {m.submitterEmail}
                          </div>
                        ) : null}
                        {m.puzzleWpId ? (
                          <div className="mt-1">
                            <Badge variant="secondary" className="font-mono text-[10px]">
                              {m.puzzleWpId}
                            </Badge>
                          </div>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500">{formatDate(m.createdAt)}</div>
                    </div>

                    {m.subject ? <div className="text-sm font-medium">{m.subject}</div> : null}
                    <p className="whitespace-pre-wrap text-sm text-gray-800">{m.message}</p>

                    {m.replies.length > 0 ? (
                      <ul className="space-y-2 border-t pt-3">
                        {m.replies.map((reply) => (
                          <li
                            key={reply.id}
                            className={cn(
                              "flex",
                              reply.direction === "OUTBOUND" ? "justify-end" : "justify-start"
                            )}
                          >
                            <div
                              className={cn(
                                "max-w-[85%] rounded-md px-3 py-2 text-sm",
                                reply.direction === "OUTBOUND"
                                  ? "bg-blue-50 text-gray-900"
                                  : "bg-gray-50 text-gray-900"
                              )}
                            >
                              <div className="mb-1 flex items-baseline justify-between gap-3 text-xs text-gray-500">
                                <span className="font-medium">{senderLabel(reply)}</span>
                                <span>{formatDate(reply.createdAt)}</span>
                              </div>
                              <p className="whitespace-pre-wrap">{reply.body}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    <ReplyComposer message={m} />

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={updating || m.status === "REPLIED"}
                        onClick={() =>
                          updateStatus({
                            variables: { input: { id: m.id, status: "REPLIED" } },
                          })
                        }
                      >
                        Mark replied
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={updating || m.status === "CLOSED"}
                        onClick={() =>
                          updateStatus({
                            variables: { input: { id: m.id, status: "CLOSED" } },
                          })
                        }
                      >
                        Close
                      </Button>
                      {m.status !== "NEW" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={updating}
                          onClick={() =>
                            updateStatus({
                              variables: { input: { id: m.id, status: "NEW" } },
                            })
                          }
                        >
                          Reopen
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </TablePageLayout>
  )
}

const ReplyComposer = ({ message }: { message: Message }) => {
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState("")
  const [sendReply, { loading, error }] = useMutation(replyCreateMutation, {
    onError: () => null,
    onCompleted: () => {
      setBody("")
      setOpen(false)
    },
  })

  if (!open) {
    return (
      <div>
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          Reply
        </Button>
      </div>
    )
  }

  const trimmed = body.trim()

  return (
    <div className="space-y-2 rounded-md border p-3">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={`Write a reply to ${message.name}…`}
        rows={4}
        autoFocus
      />
      {error ? <GraphqlError error={error} /> : null}
      <div className="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setOpen(false)
            setBody("")
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={loading || trimmed.length === 0}
          onClick={() =>
            sendReply({
              variables: { input: { id: message.id, body: trimmed } },
            })
          }
        >
          {loading ? "Sending…" : "Send reply"}
        </Button>
      </div>
    </div>
  )
}
